import { logger, sanitizeError } from "../../utils/logger.js";
import { getLoggedAuthToken, refreshToken, signOut } from "../../utils/common.js";
import { TOKEN_EXPIRED_MESSAGE } from "../../utils/apiConstants.js";

/**
 * Draft helpers for the category edit modal.
 *
 * Each subcategory receives a `clientKey` so the list key is not the array index.
 * Saved rows prefer `_id`. A row created in this session uses `new-<name>`.
 * The key is not the text currently being typed, so renaming does not remount the input.
 *
 * `editable` and `clientKey` exist only in the modal. The PUT body keeps `nombre` and `activo`.
 * A 409 means that category name is already taken.
 * An expired token refreshes the session and retries the same payload.
 * If the refresh fails, the user is signed out and sent home.
 *
 * Duplicate names are compared with a strict string match, including spacing.
 * The row being renamed is skipped in that check so saving it unchanged is allowed.
 * A new subcategory is inserted at the front of the list, not appended.
 */
export const HTTP_CONFLICT = 409;

/**
 * Empty draft used before a category is copied into the modal.
 * @returns {{nombre: string, subcategorias: Array, activo: boolean}} Blank category.
 */
export const emptyCategoryDraft = () => ({
  nombre: "",
  subcategorias: [],
  activo: true,
});

/**
 * Copies a saved category into the modal draft and stamps each subcategory with a stable key.
 * @param {Object} category - Category from the catalog.
 * @param {string} category.nombre - Category name.
 * @param {boolean} category.activo - Whether the category is active.
 * @param {Array<Object>} [category.subcategorias] - Saved subcategories.
 * @returns {Object} Draft whose subcategories include `editable: false` and a `clientKey`.
 */
export const categoryDraftFrom = (category) => ({
  nombre: category.nombre,
  activo: category.activo,
  subcategorias: (category?.subcategorias || []).map((subcategoria) => ({
    ...subcategoria,
    editable: false,
    clientKey: subcategoria._id || `saved-${subcategoria.nombre}`,
  })),
});

/**
 * Builds a subcategory that has not been saved yet.
 * @param {string} nombre - Name typed in the add box.
 * @returns {{nombre: string, activo: boolean, clientKey: string}} Active row keyed by `new-<name>`.
 */
export const buildNewSubcategory = (nombre) => ({
  nombre,
  activo: true,
  clientKey: `new-${nombre}`,
});

/**
 * Whether another row already uses this name.
 * Comparison is exact, including spaces. Pass `skipIndex` while renaming so the row can keep its own name.
 * @param {Array<{nombre: string}>} subcategorias - Current draft rows.
 * @param {string} nombre - Name to check.
 * @param {number} [skipIndex=-1] - Row to ignore, or -1 to check every row.
 * @returns {boolean} True when a different row has the same name.
 */
export const subcategoryNameTaken = (subcategorias, nombre, skipIndex = -1) =>
  subcategorias.some(
    (subcategoria, index) => index !== skipIndex && subcategoria.nombre === nombre,
  );

/**
 * Flips `activo` on one subcategory and leaves the others unchanged.
 * @param {Array<Object>} subcategorias - Current draft rows.
 * @param {number} subcategoryIndex - Row to toggle.
 * @returns {Array<Object>} New list.
 */
export const toggleSubcategoryActive = (subcategorias, subcategoryIndex) =>
  subcategorias.map((subcategoria, index) =>
    index === subcategoryIndex
      ? { ...subcategoria, activo: !subcategoria.activo }
      : subcategoria,
  );

/**
 * Opens one subcategory for rename and closes every other editor.
 * @param {Array<Object>} subcategorias - Current draft rows.
 * @param {number} subcategoryIndex - Row to edit.
 * @returns {{subcategorias: Array<Object>, tempSubcategory: Object}} Updated list and the row being typed.
 */
export const beginSubcategoryEdit = (subcategorias, subcategoryIndex) => {
  let tempSubcategory = null;
  const next = subcategorias.map((subcategoria, index) => {
    if (index === subcategoryIndex) {
      tempSubcategory = { ...subcategoria, editable: true };
      return tempSubcategory;
    }
    return { ...subcategoria, editable: false };
  });
  return { subcategorias: next, tempSubcategory };
};

/**
 * Writes the new name onto one row and leaves edit mode.
 * @param {Array<Object>} subcategorias - Current draft rows.
 * @param {number} subcategoryIndex - Row that was being renamed.
 * @param {string} nombre - Name from the edit input.
 * @returns {Array<Object>} List with that row no longer editable.
 */
export const renameSubcategory = (subcategorias, subcategoryIndex, nombre) =>
  subcategorias.map((subcategoria, index) =>
    index === subcategoryIndex
      ? { ...subcategoria, nombre, editable: false }
      : subcategoria,
  );

/**
 * Drops UI-only fields before the category is saved.
 * @param {Array<Object>} subcategorias - Draft rows, including `editable` and `clientKey`.
 * @returns {Array<{nombre: string, activo: boolean}>} Body rows for the PUT.
 */
export const persistedSubcategories = (subcategorias) =>
  subcategorias.map((subcategoria) => ({
    nombre: subcategoria.nombre,
    activo: subcategoria.activo,
  }));

/**
 * Sends the category update.
 * @param {Object} categoryData - Category id, name, active flag, and persisted subcategories.
 * @returns {Promise<Object>} `ok` with the saved category, or `missingToken`, `expired`, `conflict`, or `failed`.
 */
export const putCategory = async (categoryData) => {
  const authToken = getLoggedAuthToken();
  if (!authToken) {
    logger.error("Failed to get the token");
    return { missingToken: true };
  }

  try {
    const response = await fetch(`${process.env.REACT_APP_ROOT_API}category`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(categoryData),
    });

    if (response.ok) {
      const responseData = await response.json();
      return { ok: true, category: responseData.category };
    }

    const errorData = await response.json();
    return {
      ok: false,
      expired: errorData?.message === TOKEN_EXPIRED_MESSAGE,
      conflict: response.status === HTTP_CONFLICT,
      status: response.status,
      message: errorData.message,
    };
  } catch (error) {
    logger.error("Error:", { err: sanitizeError(error) });
    return { ok: false, failed: true };
  }
};

/**
 * Saves the category, refreshing the session once if the token is expired.
 * @param {Object} params
 * @param {Object} params.categoryData - Body for the PUT.
 * @param {function} params.navigate - Router navigate, used when refresh fails.
 * @param {function(Object): void} params.onSaved - Called with the category returned by the API.
 * @param {function(): void} params.onConflict - Called when the name already exists.
 * @returns {Promise<void>}
 */
export const saveCategoryEdits = async ({
  categoryData,
  navigate,
  onSaved,
  onConflict,
}) => {
  const persist = async () => {
    const result = await putCategory(categoryData);
    if (result.missingToken || result.failed) {
      return;
    }
    if (result.ok) {
      onSaved(result.category);
      return;
    }
    if (result.expired) {
      const newTokens = await refreshToken();
      logger.info("newTokens: ", { data: newTokens });
      if (!newTokens) {
        await signOut();
        navigate("/");
        return;
      }
      await persist();
      return;
    }
    if (result.conflict) {
      onConflict();
    }
    logger.error("Failed to create category:", {
      data: [result.status, result.message],
    });
  };

  await persist();
};
